const RALLY_ALLY_ACCOUNT_DOMAIN = "account.therallyally.com";
const RALLY_ALLY_EMAIL_SUFFIX = `@${RALLY_ALLY_ACCOUNT_DOMAIN}`;

/**
 * Firebase Email/Password auth requires an email. RallyAlly user IDs map to a stable internal email.
 * @param {string} raw
 */
export function normalizeRallyAllyAuthEmail(raw) {
  const value = String(raw ?? "").trim().toLowerCase();
  if (!value) {
    throw new Error("Enter your RallyAlly user ID or email.");
  }
  if (value.includes("@")) {
    return value;
  }
  const safe = value.replace(/[^a-z0-9._+-]/g, "");
  if (!safe) {
    throw new Error("User ID can only use letters, numbers, and . _ + -");
  }
  return `${safe}@${RALLY_ALLY_ACCOUNT_DOMAIN}`;
}

/** @param {string} [email] */
export function isRallyAllyAccountEmail(email) {
  const e = String(email ?? "").trim().toLowerCase();
  return e.endsWith(RALLY_ALLY_EMAIL_SUFFIX);
}

/** @param {string} [email] */
export function rallyAllyUserIdFromEmail(email) {
  const e = String(email ?? "").trim();
  if (!e) return "";
  const lower = e.toLowerCase();
  if (!lower.endsWith(RALLY_ALLY_EMAIL_SUFFIX)) return "";
  return e.slice(0, e.length - RALLY_ALLY_EMAIL_SUFFIX.length);
}

/**
 * Name for UI (home greeting, profile hints) — never show internal @account email.
 * @param {{ displayName?: string | null; email?: string | null }} [authUser]
 * @param {{ displayName?: string | null } | null} [userProfile]
 */
export function authUserDisplayHint(authUser, userProfile) {
  const profileName = userProfile?.displayName?.trim();
  if (profileName && !isRallyAllyAccountEmail(profileName)) return profileName;

  const authName = authUser?.displayName?.trim();
  if (authName && !isRallyAllyAccountEmail(authName)) return authName;

  const email = authUser?.email || userProfile?.email;
  if (email && isRallyAllyAccountEmail(email)) {
    const id = rallyAllyUserIdFromEmail(email);
    if (id) return id;
  }

  if (profileName) return rallyAllyUserIdFromEmail(profileName) || profileName;
  if (authName) return rallyAllyUserIdFromEmail(authName) || authName;
  return "Player";
}

/**
 * Home / lobby greeting label from profile view + auth user.
 */
export function resolveHomeGreetingName(view, authUser) {
  const name = (view?.displayName || "").trim();
  if (name && !isRallyAllyAccountEmail(name)) return name;
  if (name && isRallyAllyAccountEmail(name)) {
    const id = rallyAllyUserIdFromEmail(name);
    if (id) return id;
  }
  return authUserDisplayHint(authUser, { displayName: name, email: authUser?.email });
}

/**
 * A display name that is the RallyAlly user id (`bravo_in`), not a person's name.
 * @param {string | null | undefined} value
 */
export function loginIdFromDisplayName(value) {
  const raw = String(value || "").trim();
  if (!raw || /\s/.test(raw) || raw.includes("@")) return "";
  if (!/^[a-z0-9._+-]+$/i.test(raw)) return "";
  const lower = raw.toLowerCase();
  if (raw !== lower && !/[._+-]/.test(raw)) return "";
  return lower;
}

/**
 * Public id shown when two players share a name.
 * RallyAlly login becomes `id@account.therallyally.com`. A saved contact email is the fallback.
 * @param {object | null | undefined} player
 */
export function publicAccountEmail(player) {
  const stored = String(player?.profileExtensions?.loginId || "").trim();
  const fromStored = stored ? rallyAllyUserIdFromEmail(stored) || (stored.includes("@") ? "" : stored) : "";
  const fromName = rallyAllyUserIdFromEmail(player?.displayName) || loginIdFromDisplayName(player?.displayName);
  const id = fromStored || fromName;
  if (id) return `${id.toLowerCase()}@${RALLY_ALLY_ACCOUNT_DOMAIN}`;
  return String(player?.contactEmail || "").trim();
}

export { RALLY_ALLY_ACCOUNT_DOMAIN };
